import { floatVal, intVal, Nullable } from "@jamesgmarks/utilities";
import {
  Grid,
  Paper,
  SelectChangeEvent,
  Table as MuiTable,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Button,
  Chip,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  ListItemText,
  TableSortLabel,
  Tooltip,
  Fab,
  Stack,
} from "@mui/material";
import { useEffect, useMemo, useState, useCallback } from "react";
import {
  IQueryCommandOptions,
  INestedWhereCommand,
  IWhereCommand,
  Equal,
  GreaterThanOrEqual,
  LessThanOrEqual,
  In,
  Like,
  Null,
  Or,
  TOrderDirection,
  TWhereCondition,
} from '@llws/dynamic-router-utils';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import ClearIcon from '@mui/icons-material/Clear';
import { IClientDropdownItem } from "../../../entity-interfaces/IClientDropdownItem";
import { getMany } from "../../../redux/features/dynamic/actions";
import { loadServices } from "../../../redux/features/products/actions";
import { loadBillingFrequenciesLookup, loadBillingTypesLookup } from "../../../redux/features/lookups/actions";
import { useAppSelector } from "../../../redux/hooks";
import { ClientDropdown } from "../../parts/ClientDropdown";
import { StyledTableCell, StyledTableRow } from "../../parts/mui/StyledTables";
import { Spinner } from "../../parts/Spinner";
import { addDays, getDiscountRateFromSubscription, getMonthEnd, getMonthStart, makeYmd } from "src/app-utils";
import { sortSalesRepsByName } from "src/app-utils/sales-reps";
import { rowsPerPageList } from "../Clients/Tabs";
import { ISubscriptions } from "@llws/lift-entity-interfaces";

const SUBSCRIPTION_STATUSES = ['Active', 'Expiring This Month', 'Expired This Month', 'Expired'] as const;

/** Union type representing the possible display statuses of a subscription. */
type TSubscriptionStatus = typeof SUBSCRIPTION_STATUSES[number];

/**
 * Type guard that checks whether a string is a valid `TSubscriptionStatus`.
 * @param s - The string to check.
 * @returns `true` if `s` is one of the recognised subscription status values.
 */
const isSubscriptionStatus = (s: string): s is TSubscriptionStatus => {
  return SUBSCRIPTION_STATUSES.some(status => status === s);
};

/**
 * Computes a set of `Date` boundary values relative to the current date that are
 * used to classify subscriptions by their expiry status.
 *
 * Boundaries returned:
 * - `today` – midnight of the current calendar day.
 * - `tomorrow` – midnight of the next calendar day.
 * - `currentMonthStart` – first day of the current calendar month.
 * - `lastDayPrevMonth` – last day of the previous calendar month.
 * - `nextMonthStart` – first day of the next calendar month.
 * - `lastDayCurrentMonth` – last day of the current calendar month.
 */
const getSubscriptionDateBoundaries = () => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = addDays(today, 1);
  const currentMonthStart = getMonthStart(now.getFullYear(), now.getMonth() + 1);
  const lastDayPrevMonth = getMonthEnd(now.getFullYear(), now.getMonth(), false);
  const nextMonthStart = getMonthStart(now.getFullYear(), now.getMonth() + 2);
  const lastDayCurrentMonth = getMonthEnd(now.getFullYear(), now.getMonth() + 1, false);
  return {
    today, tomorrow, currentMonthStart, lastDayPrevMonth, nextMonthStart, lastDayCurrentMonth, 
  };
};

/**
 * Derives the display status of a subscription from its expiry date.
 *
 * Classification logic:
 * - No expiry date → `'Active'`
 * - Expiry is on or after the start of next month → `'Active'`
 * - Expiry is within the current month and in the past (before today) → `'Expired This Month'`
 * - Expiry is within the current month and still upcoming → `'Expiring This Month'`
 * - Expiry is before the start of the current month → `'Expired'`
 *
 * @param expiryDate - ISO date string of the subscription's expiry date, or null/undefined.
 * @returns The `TSubscriptionStatus` label for the subscription.
 */
const getSubscriptionStatusFromDate = (expiryDate: string | null | undefined): TSubscriptionStatus => {
  if (!expiryDate) return 'Active';

  const { tomorrow, currentMonthStart, nextMonthStart } = getSubscriptionDateBoundaries();
  const expiry = new Date(expiryDate);

  if (expiry >= nextMonthStart) return 'Active';
  if (expiry >= currentMonthStart) {
    return expiry < tomorrow ? 'Expired This Month' : 'Expiring This Month';
  }
  return 'Expired';
};

/**
 * Translates a `TSubscriptionStatus` label into the corresponding array of
 * `dynamic-router-utils` where-conditions suitable for filtering subscriptions
 * by `expiryDate` in an API query.
 *
 * - `'Active'` – no expiry date **or** expiry on/after next month start.
 * - `'Expiring This Month'` – expiry between tomorrow and the last day of the current month (inclusive).
 * - `'Expired This Month'` – expiry between the first day of the current month and today (inclusive).
 * - `'Expired'` – expiry on or before the last day of the previous month.
 *
 * @param status - The subscription status to convert.
 * @returns An array of where-commands that can be spread into a query's `where` array.
 */
const getStatusDateConditions = (status: TSubscriptionStatus): (IWhereCommand | INestedWhereCommand)[] => {
  const {
    today, tomorrow, currentMonthStart, lastDayPrevMonth, nextMonthStart, lastDayCurrentMonth, 
  } = getSubscriptionDateBoundaries();

  const condition: TWhereCondition = 'and';

  const statusConditionMap: Record<TSubscriptionStatus, (IWhereCommand | INestedWhereCommand)[]> = {
    'Active': [{
      condition,
      whereCommands: [Or(Null('expiryDate')), Or(GreaterThanOrEqual('expiryDate', makeYmd(nextMonthStart, true)))],
    }],
    'Expiring This Month': [
      GreaterThanOrEqual('expiryDate', makeYmd(tomorrow, true)),
      LessThanOrEqual('expiryDate', makeYmd(lastDayCurrentMonth, true)),
    ],
    'Expired This Month': [
      GreaterThanOrEqual('expiryDate', makeYmd(currentMonthStart, true)),
      LessThanOrEqual('expiryDate', makeYmd(today, true)),
    ],
    'Expired': [LessThanOrEqual('expiryDate', makeYmd(lastDayPrevMonth, true))],
  };

  return statusConditionMap[status];
};

const statusColorMap: Record<TSubscriptionStatus, string> = {
  'Active': '#4caf50',
  'Expiring This Month': '#ff9800',
  'Expired This Month': '#f44336',
  'Expired': '#9e9e9e',
};

/**
 * Returns the display label for a service, preferring the invoice item name over
 * the generic service name and falling back to `'Unknown'` when neither is set.
 *
 * @param svc - Partial service object with optional `invoiceItem` and `name` fields.
 * @returns The resolved display label string.
 */
const getServiceLabel = (svc?: { invoiceItem?: string | null; name?: string | null }): string => {
  return svc?.invoiceItem ?? svc?.name ?? 'Unknown';
};

/**
 * Returns the display name for a partner, falling back to `'Unknown'` when the
 * partner's name is absent.
 *
 * @param partner - Partial partner object with an optional `name` field.
 * @returns The resolved display name string.
 */
const getPartnerName = (partner?: { name?: string | null }): string => {
  return partner?.name ?? 'Unknown';
};

/**
 * Constructs a display name string for a sales rep from their associated user
 * record's first and last name fields.  Falls back to `'Unknown'` for a missing
 * first name and silently omits a missing last name.
 *
 * @param rep - Partial sales rep object whose `user` relation carries `firstName`
 *   and `lastName`.
 * @returns The trimmed full-name string.
 */
const getSalesRepName = (rep?: { user?: { firstName?: string | null; lastName?: string | null } }): string => {
  return `${rep?.user?.firstName ?? 'Unknown'} ${rep?.user?.lastName ?? ''}`.trim();
};

/**
 * Base query options used when fetching subscription data. Eagerly loads all
 * relations needed to render the list rows and support filter dropdowns without
 * additional round-trips.
 *
 * Aliases used in filter conditions built by `searchAndFilters`:
 * - `s`   – subscription root
 * - `c`   – client
 * - `bs`  – base subscription (carries `partnerId` and `serviceId`)
 * - `shb` – subscriptions-has-buildings join
 * - `svc` – service (carries `billingTypeId`)
 * - `p`   – partner
 * - `fq`  – billing frequency
 * - `bt`  – billing type
 * - `sr`  – sales rep
 * - `u`   – sales rep's user record (name fields)
 */
const relationsQueryCommand: IQueryCommandOptions = {
  alias: 's',
  relations: [
    { path: 's.client', alias: 'c' },
    { path: 's.baseSubscription', alias: 'bs' },
    { path: 's.subscriptionsHasBuildings', alias: 'shb' },
    { path: 'bs.service', alias: 'svc' },
    { path: 'bs.partner', alias: 'p' },
    { path: 's.billingFrequency', alias: 'fq' },
    { path: 'svc.billingType', alias: 'bt' },
    { path: 's.salesRep', alias: 'sr' },
    { path: 'sr.user', alias: 'u' },
  ],
};

/**
 * Renders a single row in the subscription list table.
 *
 * Displays key subscription details: ID, client name, service/partner label,
 * invoice description, billing frequency and type, base price, discount rate,
 * start date, expiry date, computed status badge, rate-expiry date, sales rep,
 * and action buttons (View in Lift, open new editor).
 *
 * @param subscription - The subscription record to render, including eagerly-loaded
 *   relations (`baseSubscription`, `client`, `billingFrequency`, `salesRep`, etc.).
 */
export const SubscriptionListRow = ({ subscription }: { subscription: ISubscriptions; }) => {
  const baseSubscription = subscription.baseSubscription;
  if (!baseSubscription) {
    console.warn('Base SUBSCRIPTION', { subscription });
    return <div>No base subscription</div>;
  }
  const service = baseSubscription.service;
  if (!service) {
    console.warn({ service });
    return <div>No service for base subscription</div>;
  }

  const status = getSubscriptionStatusFromDate(subscription.expiryDate);

  return (
    <StyledTableRow
      customSx={{ userSelect: 'none' }}
      dark={true}
      hover={true}
    >
      <StyledTableCell
        dark={true}
        style={{ textAlign: 'center' }}
      >
        {subscription.id}
      </StyledTableCell>
      <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
        {subscription.client?.name}
      </StyledTableCell>
      <StyledTableCell dark={true} style={{ textAlign: 'left' }}>
        {service.invoiceItem ?? baseSubscription.partner?.name}
      </StyledTableCell>
      <StyledTableCell dark={true} style={{ textAlign: 'left' }}>
        {subscription.invoiceDescription}
      </StyledTableCell>
      <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
        {subscription.billingFrequency?.name} - {service.billingType?.name ?? 'N/A'}
      </StyledTableCell>
      <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
        {subscription.basePrice}
      </StyledTableCell>
      <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
        {`${floatVal(getDiscountRateFromSubscription(subscription)) * 100}%`}
      </StyledTableCell>
      <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
        {subscription.startDate}
      </StyledTableCell>
      <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
        {subscription.expiryDate ?? '------'}
      </StyledTableCell>
      <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
        <span style={{ color: statusColorMap[status], fontWeight: 'bold' }}>
          {status}
        </span>
      </StyledTableCell>
      <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
        {(
          subscription.rateExpiryDate == null
          || subscription.rateExpiryDate.toString() === '0000-00-00'
          || subscription.rateExpiryDate.toString() === '1899-11-30'
        ) ? '------' : subscription.rateExpiryDate.toString()
        }
      </StyledTableCell>
      <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
        {subscription.salesRep?.user?.firstName} {subscription.salesRep?.user?.lastName}
      </StyledTableCell>

      <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Button
            variant="outlined"
            size="small"
            sx={{
              color: '#ffffff',
              borderColor: '#ffffff',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderColor: '#ffffff',
              },
            }}
            onClick={() => window.open(
              `https://superlift.theliftsystem.com/subscriptions/view/${subscription.id}`,
              '_blank',
            )}
          >
            View
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={{
              color: '#ffffff',
              borderColor: '#ffffff',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderColor: '#ffffff',
              },
            }}
            onClick={() => window.open(
              `/subscriptions/form/${subscription.id}`,
              '_blank',
            )}
          >
            Try New Editor
          </Button>
        </Box>
      </StyledTableCell>
    </StyledTableRow>
  );
};

/**
 * Paginated, filterable, and sortable list of all subscriptions in the system.
 *
 * ## Filtering
 * Filters are applied server-side via the dynamic-router query API. Active filter
 * criteria are surfaced as dismissible `Chip` badges below the search bar.
 * Available filters:
 * - **Client** – autocomplete dropdown
 * - **Status** – Active / Expiring This Month / Expired This Month / Expired
 *   (translated to `expiryDate` range conditions by `getStatusDateConditions`)
 * - **Sales Rep** – multi-select
 * - **Partner** – multi-select
 * - **Service** – multi-select, sorted alphabetically by invoice item name
 * - **Billing Frequency** – multi-select, loaded from the lookups API
 * - **Billing Type** – multi-select, loaded from the lookups API
 * - **Subscription ID** – numeric exact-match search
 * - **Invoice Description** – debounced (500 ms) substring search
 * - **Advanced filters** (collapsible accordion):
 *   - Start Date range (from / to)
 *   - Expiry Date range (from / to)
 *   - Rate Expiry Date range (from / to)
 *   - Base Price range (min / max)
 *
 * ## Sorting
 * Column headers are clickable; clicking the same column toggles asc/desc order.
 * Any filter change resets the page to 0.
 *
 * ## Data loading
 * Reference data (sales reps, partners, services, billing frequencies, billing types)
 * is loaded on mount via dedicated Redux actions and cached in the store so subsequent
 * renders do not re-fetch.
 */
export const SubscriptionList = () => {

  const loadedState = useAppSelector((state) => state.dynamic.data.subscriptions?.loadedState);
  const subscriptions = useAppSelector((state) => state.dynamic.data.subscriptions?.list);
  const subscriptionMetadata = useAppSelector((state) => state.dynamic.metaData);
  const salesReps = useAppSelector((state) => state.dynamic.data.sales_reps?.list ?? []);
  // Display order for the filter dropdown; `salesReps` is kept for id-based lookups below.
  const sortedSalesReps = useMemo(() => sortSalesRepsByName(salesReps), [salesReps]);
  const partners = useAppSelector((state) => state.dynamic.data.partners?.list ?? []);
  const services = useAppSelector((state) => state.products.services ?? []);
  const billingFrequencies = useAppSelector((state) => state.lookups.billingFrequencies ?? []);
  const billingTypes = useAppSelector((state) => state.lookups.billingTypes ?? []);

  // Search and filter states
  const [searchClient, setSearchClient] = useState<Nullable<IClientDropdownItem>>(null);
  const [searchField, setSearchField] = useState<Nullable<string>>(null);
  const [debouncedSearchField, setDebouncedSearchField] = useState<Nullable<string>>(null);
  const [salesRepFilter, setSalesRepFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [partnerFilter, setPartnerFilter] = useState<string[]>([]);
  const [serviceFilter, setServiceFilter] = useState<string[]>([]);
  const [billingFrequencyFilter, setBillingFrequencyFilter] = useState<string[]>([]);
  const [billingTypeFilter, setBillingTypeFilter] = useState<string[]>([]);
  const [subscriptionIdSearch, setSubscriptionIdSearch] = useState<string>('');
  const [dateRangeFilter, setDateRangeFilter] = useState({
    startDateFrom: '',
    startDateTo: '',
    expiryDateFrom: '',
    expiryDateTo: '',
    rateExpiryDateFrom: '',
    rateExpiryDateTo: '',
  });
  const [basePriceRange, setBasePriceRange] = useState({ min: '', max: '' });

  const [showSearch] = useState<boolean>(true);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0); // 0-based for Material-UI pagination
  const [limit, setLimit] = useState<number>(20);
  const [sortOrder, setSortOrder] = useState<TOrderDirection>('asc');
  const [sortBy, setSortBy] = useState<string>('id');

  /**
   * Returns the MUI `TableSortLabel` direction prop for a given column field.
   *
   * Non-active columns return `'asc'` rather than `undefined` because MUI requires
   * a valid direction string on every header regardless of whether it is the active
   * sort.  `undefined` is returned only to satisfy TypeScript in the unreachable
   * fall-through case where neither branch matches.
   *
   * @param field - The sort-field path to compare against the active `sortBy` state.
   * @returns `'asc'` | `'desc'` | `undefined`
   */
  const getTableSortDirection = (field: string): 'asc' | 'desc' | undefined => {
    if (sortBy !== field) return 'asc';
    if (sortOrder === 'asc') return 'asc';
    if (sortOrder === 'desc') return 'desc';
    return undefined; // need this undefined because of the typing difference between material ui and DR sort direction
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchField(searchField);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchField]);

  const resetPageOnFilterChange = useCallback(() => {
    setPage(0);
  }, []);

  // Handle sorting
  /**
   * Handles a sortable column header click.  If the clicked `field` is already
   * the active sort column the direction toggles between ascending and descending;
   * otherwise switches to the new field starting in ascending order.  Always
   * resets the page to 0 so users see the first page of the re-sorted results.
   *
   * @param field - The dynamic-router field path to sort by (e.g. `'id'`, `'c.name'`).
   */
  const handleSort = useCallback((field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(0);
  }, [sortBy, sortOrder]);

  useEffect(resetPageOnFilterChange, [
    searchClient,
    debouncedSearchField,
    salesRepFilter,
    statusFilter,
    partnerFilter,
    serviceFilter,
    billingFrequencyFilter,
    billingTypeFilter,
    subscriptionIdSearch,
    dateRangeFilter,
    basePriceRange,
    resetPageOnFilterChange,
  ]);

  /**
   * Resets every filter and search field to its initial empty state and returns
   * pagination to page 1, effectively showing all subscriptions unfiltered.
   *
   * Invoked by the "Clear (N)" button in the filter bar and by the "Clear All
   * Filters" button inside the empty-state message when no results are found.
   */
  const clearAllFilters = useCallback(() => {
    setSearchClient(null);
    setSearchField(null);
    setDebouncedSearchField(null);
    setSalesRepFilter([]);
    setStatusFilter('');
    setPartnerFilter([]);
    setServiceFilter([]);
    setBillingFrequencyFilter([]);
    setBillingTypeFilter([]);
    setSubscriptionIdSearch('');
    setDateRangeFilter({
      startDateFrom: '',
      startDateTo: '',
      expiryDateFrom: '',
      expiryDateTo: '',
      rateExpiryDateFrom: '',
      rateExpiryDateTo: '',
    });
    setBasePriceRange({ min: '', max: '' });
    setPage(1);
  }, []);

  /**
   * Memoized count of how many distinct filter categories are currently active.
   * Used to render the "Clear (N)" badge on the filter toolbar and the
   * "(N filters applied)" suffix in the results-summary line.
   */
  // Count active filters
  const activeFiltersCount = useMemo(() => {
    return [
      ...(searchClient ? ['client'] : []),
      ...(debouncedSearchField ? ['description'] : []),
      ...(salesRepFilter.length > 0 ? ['salesRep'] : []),
      ...(statusFilter ? ['status'] : []),
      ...(partnerFilter.length > 0 ? ['partner'] : []),
      ...(serviceFilter.length > 0 ? ['service'] : []),
      ...(billingFrequencyFilter.length > 0 ? ['billingFrequency'] : []),
      ...(billingTypeFilter.length > 0 ? ['billingType'] : []),
      ...(subscriptionIdSearch ? ['id'] : []),
      ...(dateRangeFilter.startDateFrom || dateRangeFilter.startDateTo ? ['startDate'] : []),
      ...(dateRangeFilter.expiryDateFrom || dateRangeFilter.expiryDateTo ? ['expiryDate'] : []),
      ...(dateRangeFilter.rateExpiryDateFrom || dateRangeFilter.rateExpiryDateTo ? ['rateExpiryDate'] : []),
      ...(basePriceRange.min || basePriceRange.max ? ['basePrice'] : []),
    ].length;
  }, [
    searchClient,
    debouncedSearchField,
    salesRepFilter,
    statusFilter,
    partnerFilter,
    serviceFilter,
    billingFrequencyFilter,
    billingTypeFilter,
    subscriptionIdSearch,
    dateRangeFilter,
    basePriceRange,
  ]);

  useEffect(() => { getMany('subscriptions', relationsQueryCommand); }, []);

  // Load sales reps for filter
  useEffect(() => {
    if (salesReps.length === 0) {
      getMany('sales_reps', {
        alias: 'sr',
        relations: [{ path: 'sr.user', alias: 'u' }],
        where: [Equal('status', 'active')],
      });
    }
  }, [salesReps.length]);

  // Load partners for filter
  useEffect(() => {
    if (partners.length === 0) {
      getMany('partners');
    }
  }, [partners.length]);

  // Load services for filter
  useEffect(() => {
    if (services.length === 0) {
      loadServices();
    }
  }, [services.length]);

  // Load billing frequencies for filter
  useEffect(() => {
    if (billingFrequencies.length === 0) {
      loadBillingFrequenciesLookup();
    }
  }, [billingFrequencies.length]);

  // Load billing types for filter
  useEffect(() => {
    if (billingTypes.length === 0) {
      loadBillingTypesLookup();
    }
  }, [billingTypes.length]);

  const sortedServices = useMemo(() => {
    return [...services].sort((a, b) => {
      return (a.invoiceItem ?? a.name ?? '').localeCompare(b.invoiceItem ?? b.name ?? '');
    });
  }, [services]);

  /**
   * Memoized query options object sent to `getMany('subscriptions', ...)` whenever
   * any filter, sort, or pagination state changes.
   *
   * Builds the `where` array from all active filter states, combining:
   * - Exact-match filters (ID, client, billing frequency, billing type)
   * - Status-derived date-range conditions via `getStatusDateConditions`
   * - Multi-value IN filters (partner, service, sales rep, billing frequency, billing type)
   * - Date-range bounds (startDate, expiryDate, rateExpiryDate)
   * - Numeric range bounds (basePrice min/max, parsed with `floatVal`)
   * - Debounced substring match on `invoiceDescription`
   *
   * All filter conditions are AND-combined by the dynamic-router API.
   */
  const searchAndFilters: IQueryCommandOptions = useMemo(() => {
    const filterConditions = [
      // Subscription ID filter
      ...(subscriptionIdSearch ? [Equal('id', intVal(subscriptionIdSearch))] : []),

      // Client filter
      ...(searchClient ? [Equal('clientId', searchClient.id)] : []),

      // Status filter (translates status label into date-range conditions)
      ...(statusFilter && isSubscriptionStatus(statusFilter) ? getStatusDateConditions(statusFilter) : []),

      // Partner filter
      ...(partnerFilter.length > 0 ? [In('bs.partnerId', partnerFilter.map(id => intVal(id)))] : []),

      // Service filter
      ...(serviceFilter.length > 0 ? [In('bs.serviceId', serviceFilter.map(id => intVal(id)))] : []),

      // Sales rep filter
      ...(salesRepFilter.length > 0 ? [In('salesRepId', salesRepFilter.map(id => intVal(id)))] : []),

      // Billing frequency filter
      ...(billingFrequencyFilter.length > 0 ? [In('billingFrequencyId', billingFrequencyFilter.map(id => intVal(id)))] : []),

      // Billing type filter
      ...(billingTypeFilter.length > 0 ? [In('svc.billingTypeId', billingTypeFilter.map(id => intVal(id)))] : []),

      // Date range filters
      ...(dateRangeFilter.startDateFrom ? [GreaterThanOrEqual('startDate', dateRangeFilter.startDateFrom)] : []),
      ...(dateRangeFilter.startDateTo ? [LessThanOrEqual('startDate', dateRangeFilter.startDateTo)] : []),
      ...(dateRangeFilter.expiryDateFrom ? [GreaterThanOrEqual('expiryDate', dateRangeFilter.expiryDateFrom)] : []),
      ...(dateRangeFilter.expiryDateTo ? [LessThanOrEqual('expiryDate', dateRangeFilter.expiryDateTo)] : []),
      ...(dateRangeFilter.rateExpiryDateFrom ? [GreaterThanOrEqual('rateExpiryDate', dateRangeFilter.rateExpiryDateFrom)] : []),
      ...(dateRangeFilter.rateExpiryDateTo ? [LessThanOrEqual('rateExpiryDate', dateRangeFilter.rateExpiryDateTo)] : []),

      // Base price range filters
      ...(basePriceRange.min ? [GreaterThanOrEqual('basePrice', floatVal(basePriceRange.min))] : []),
      ...(basePriceRange.max ? [LessThanOrEqual('basePrice', floatVal(basePriceRange.max))] : []),

      // Description search
      ...(debouncedSearchField ? [Like('invoiceDescription', `%${debouncedSearchField}%`)] : []),
    ];

    return {
      ...relationsQueryCommand,
      where: filterConditions,
      orderBy: [{ field: sortBy, direction: sortOrder }],
      page: page + 1,
      limit,
    };
  }, [
    subscriptionIdSearch,
    searchClient,
    debouncedSearchField,
    salesRepFilter,
    statusFilter,
    partnerFilter,
    serviceFilter,
    billingFrequencyFilter,
    billingTypeFilter,
    dateRangeFilter,
    basePriceRange,
    sortBy,
    sortOrder,
    page,
    limit,
  ]);

  useEffect(() => { getMany('subscriptions', searchAndFilters); }, [searchAndFilters]);

  // Pagination rows handler
  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setLimit(intVal(event.target.value));
    setPage(0);
  };

  return (
    <>
      <header className="Section-header">Subscription List</header>
      <Grid container spacing={2} mt={2} mb={2} justifyContent="center" alignItems="center">
        <Grid item xs={12}>
          {showSearch && (
            <Paper variant='outlined' sx={{ padding: '1rem', backgroundColor: '#e0e0e0' }}>
              {/* Basic Search */}
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" gutterBottom>Client:</Typography>
                  <ClientDropdown
                    clientId={searchClient?.id ? intVal(searchClient.id) : null}
                    onClientChanged={(client) => { setSearchClient(client); }}
                    onClientFreeForm={() => { }}
                    key={searchClient?.id ?? 'no-client'}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" gutterBottom>Status:</Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      sx={{ background: "white" }}
                      displayEmpty
                    >
                      <MenuItem value=""><em>All Statuses</em></MenuItem>
                      {SUBSCRIPTION_STATUSES.map((status) => (
                        <MenuItem key={status} value={status}>{status}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" gutterBottom>Description:</Typography>
                  <TextField
                    placeholder="Search by description..."
                    type="text"
                    sx={{ background: "white" }}
                    size="small"
                    fullWidth={true}
                    value={searchField ?? ''}
                    onChange={(e) => setSearchField(e.target.value || null)}
                    helperText={searchField && searchField !== debouncedSearchField ? "Searching..." : ""}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" gutterBottom>Subscription ID:</Typography>
                  <TextField
                    placeholder="Filter by ID..."
                    type="number"
                    sx={{ background: "white" }}
                    size="small"
                    fullWidth={true}
                    value={subscriptionIdSearch}
                    onChange={(e) => setSubscriptionIdSearch(e.target.value)}
                    inputProps={{ min: 1, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" gutterBottom>Sales Rep:</Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      multiple
                      value={salesRepFilter}
                      onChange={(e: SelectChangeEvent<string[]>) => {
                        const value = e.target.value;
                        setSalesRepFilter(Array.isArray(value) ? value : value.split(','));
                      }}
                      sx={{ background: "white" }}
                      displayEmpty
                      renderValue={(selected) => {
                        if (selected.length === 0) return <em>All Sales Reps</em>;
                        if (selected.length === 1) {
                          return getSalesRepName(salesReps.find(sr => sr.id?.toString() === selected[0]));
                        }
                        return `${selected.length} selected`;
                      }}
                    >
                      {sortedSalesReps.filter(sr => sr.id != null).map((salesRep) => {
                        const idStr = `${salesRep.id}`;
                        return (
                          <MenuItem key={idStr} value={idStr}>
                            <Checkbox checked={salesRepFilter.includes(idStr)} />
                            <ListItemText primary={`${salesRep.user.firstName} ${salesRep.user.lastName}`} />
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" gutterBottom>Partner:</Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      multiple
                      value={partnerFilter}
                      onChange={(e: SelectChangeEvent<string[]>) => {
                        const value = e.target.value;
                        setPartnerFilter(Array.isArray(value) ? value : value.split(','));
                      }}
                      sx={{ background: "white" }}
                      displayEmpty
                      renderValue={(selected) => {
                        if (selected.length === 0) return <em>All Partners</em>;
                        if (selected.length === 1) {
                          return getPartnerName(partners.find(partner => partner.id?.toString() === selected[0]));
                        }
                        return `${selected.length} selected`;
                      }}
                    >
                      {partners.filter(p => p.id != null).map((partner) => {
                        const idStr = `${partner.id}`;
                        return (
                          <MenuItem key={idStr} value={idStr}>
                            <Checkbox checked={partnerFilter.includes(idStr)} />
                            <ListItemText primary={partner.name} />
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" gutterBottom>Service:</Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      multiple
                      value={serviceFilter}
                      onChange={(e: SelectChangeEvent<string[]>) => {
                        const value = e.target.value;
                        setServiceFilter(Array.isArray(value) ? value : value.split(','));
                      }}
                      sx={{ background: "white" }}
                      displayEmpty
                      renderValue={(selected) => {
                        if (selected.length === 0) return <em>All Services</em>;
                        if (selected.length === 1) {
                          return getServiceLabel(sortedServices.find(s => `${s.id}` === selected[0]));
                        }
                        return `${selected.length} selected`;
                      }}
                    >
                      {sortedServices.filter(s => s.id != null).map((svc) => {
                        const idStr = `${svc.id}`;
                        return (
                          <MenuItem key={idStr} value={idStr}>
                            <Checkbox checked={serviceFilter.includes(idStr)} />
                            <ListItemText primary={svc.invoiceItem ?? svc.name} />
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" gutterBottom>Billing Frequency:</Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      multiple
                      value={billingFrequencyFilter}
                      onChange={(e: SelectChangeEvent<string[]>) => {
                        const value = e.target.value;
                        setBillingFrequencyFilter(Array.isArray(value) ? value : value.split(','));
                      }}
                      sx={{ background: "white" }}
                      displayEmpty
                      renderValue={(selected) => {
                        if (selected.length === 0) return <em>All Frequencies</em>;
                        if (selected.length === 1) {
                          return billingFrequencies.find(bf => `${bf.id}` === selected[0])?.name ?? 'Unknown';
                        }
                        return `${selected.length} selected`;
                      }}
                    >
                      {billingFrequencies.filter(bf => bf.id != null).map((bf) => {
                        const idStr = `${bf.id}`;
                        return (
                          <MenuItem key={idStr} value={idStr}>
                            <Checkbox checked={billingFrequencyFilter.includes(idStr)} />
                            <ListItemText primary={bf.name} />
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" gutterBottom>Billing Type:</Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      multiple
                      value={billingTypeFilter}
                      onChange={(e: SelectChangeEvent<string[]>) => {
                        const value = e.target.value;
                        setBillingTypeFilter(Array.isArray(value) ? value : value.split(','));
                      }}
                      sx={{ background: "white" }}
                      displayEmpty
                      renderValue={(selected) => {
                        if (selected.length === 0) return <em>All Billing Types</em>;
                        if (selected.length === 1) {
                          return billingTypes.find(bt => `${bt.id}` === selected[0])?.name ?? 'Unknown';
                        }
                        return `${selected.length} selected`;
                      }}
                    >
                      {billingTypes.filter(bt => bt.id != null).map((bt) => {
                        const idStr = `${bt.id}`;
                        return (
                          <MenuItem key={idStr} value={idStr}>
                            <Checkbox checked={billingTypeFilter.includes(idStr)} />
                            <ListItemText primary={bt.name} />
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, mt: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                      startIcon={<ExpandMoreIcon />}
                    >
                      {showAdvancedFilters ? 'Hide' : 'Show'} Advanced Filters
                    </Button>
                    {activeFiltersCount > 0 && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={clearAllFilters}
                        startIcon={<ClearIcon />}
                        color="error"
                      >
                        Clear ({activeFiltersCount})
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {/* Advanced Filters */}
              {showAdvancedFilters && (
                <Accordion
                  expanded={showAdvancedFilters}
                  sx={{
                    mt: 2,
                    backgroundColor: '#f0f7ff',
                    boxShadow: 'none',
                    border: '1px solid #b3d9ff',
                    borderRadius: '8px',
                    '&.MuiAccordion-root': {
                      backgroundColor: '#f0f7ff',
                    },
                    '& .MuiAccordionDetails-root': {
                      backgroundColor: '#f0f7ff',
                      padding: '16px',
                    },
                  }}
                >
                  <AccordionSummary sx={{ padding: '8px 16px', minHeight: 'auto' }}>
                    <Typography variant="subtitle1">Advanced Filters</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#555', mb: 1 }}>
                          Start Date Range
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" gutterBottom>Start Date From:</Typography>
                        <TextField
                          type="date"
                          size="small"
                          fullWidth
                          value={dateRangeFilter.startDateFrom}
                          onChange={(e) => setDateRangeFilter(prev => ({
                            ...prev,
                            startDateFrom: e.target.value,
                          }))}
                          sx={{ background: "white" }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" gutterBottom>Start Date To:</Typography>
                        <TextField
                          type="date"
                          size="small"
                          fullWidth
                          value={dateRangeFilter.startDateTo}
                          onChange={(e) => setDateRangeFilter(prev => ({
                            ...prev,
                            startDateTo: e.target.value,
                          }))}
                          sx={{ background: "white" }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#555', mb: 1 }}>
                          Expiry Date Range
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" gutterBottom>Expiry Date From:</Typography>
                        <TextField
                          type="date"
                          size="small"
                          fullWidth
                          value={dateRangeFilter.expiryDateFrom}
                          onChange={(e) => setDateRangeFilter(prev => ({
                            ...prev,
                            expiryDateFrom: e.target.value,
                          }))}
                          sx={{ background: "white" }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" gutterBottom>Expiry Date To:</Typography>
                        <TextField
                          type="date"
                          size="small"
                          fullWidth
                          value={dateRangeFilter.expiryDateTo}
                          onChange={(e) => setDateRangeFilter(prev => ({
                            ...prev,
                            expiryDateTo: e.target.value,
                          }))}
                          sx={{ background: "white" }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#555', mb: 1 }}>
                          Rate Expiry Date Range
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" gutterBottom>Rate Expiry From:</Typography>
                        <TextField
                          type="date"
                          size="small"
                          fullWidth
                          value={dateRangeFilter.rateExpiryDateFrom}
                          onChange={(e) => setDateRangeFilter(prev => ({
                            ...prev,
                            rateExpiryDateFrom: e.target.value,
                          }))}
                          sx={{ background: "white" }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" gutterBottom>Rate Expiry To:</Typography>
                        <TextField
                          type="date"
                          size="small"
                          fullWidth
                          value={dateRangeFilter.rateExpiryDateTo}
                          onChange={(e) => setDateRangeFilter(prev => ({
                            ...prev,
                            rateExpiryDateTo: e.target.value,
                          }))}
                          sx={{ background: "white" }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#555', mb: 1 }}>
                          Base Price Range
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" gutterBottom>Min Price:</Typography>
                        <TextField
                          type="number"
                          size="small"
                          fullWidth
                          placeholder="0.00"
                          value={basePriceRange.min}
                          onChange={(e) => setBasePriceRange(prev => ({ ...prev, min: e.target.value }))}
                          sx={{ background: "white" }}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" gutterBottom>Max Price:</Typography>
                        <TextField
                          type="number"
                          size="small"
                          fullWidth
                          placeholder="0.00"
                          value={basePriceRange.max}
                          onChange={(e) => setBasePriceRange(prev => ({ ...prev, max: e.target.value }))}
                          sx={{ background: "white" }}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Active Filters Display */}
              {activeFiltersCount > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Active Filters:</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {searchClient && (
                      <Chip
                        label={`Client: ${searchClient.name}`}
                        onDelete={() => setSearchClient(null)}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {statusFilter && (
                      <Chip
                        label={`Status: ${statusFilter}`}
                        onDelete={() => setStatusFilter('')}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {subscriptionIdSearch && (
                      <Chip
                        label={`ID: ${subscriptionIdSearch}`}
                        onDelete={() => setSubscriptionIdSearch('')}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {partnerFilter.length > 0 && (
                      <Chip
                        label={
                          `Partner: ${partnerFilter.length === 1
                            ? getPartnerName(partners.find(p => p.id?.toString() === partnerFilter[0]))
                            : `${partnerFilter.length} selected`
                          }`
                        }
                        onDelete={() => setPartnerFilter([])}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {serviceFilter.length > 0 && (
                      <Chip
                        label={
                          `Service: ${serviceFilter.length === 1
                            ? getServiceLabel(sortedServices.find(s => s.id?.toString() === serviceFilter[0]))
                            : `${serviceFilter.length} selected`
                          }`
                        }
                        onDelete={() => setServiceFilter([])}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {billingFrequencyFilter.length > 0 && (
                      <Chip
                        label={
                          `Billing Freq: ${billingFrequencyFilter.length === 1
                            ? (billingFrequencies.find(bf => bf.id?.toString() === billingFrequencyFilter[0])?.name ?? 'Unknown')
                            : `${billingFrequencyFilter.length} selected`
                          }`
                        }
                        onDelete={() => setBillingFrequencyFilter([])}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {billingTypeFilter.length > 0 && (
                      <Chip
                        label={
                          `Billing Type: ${billingTypeFilter.length === 1
                            ? (billingTypes.find(bt => bt.id?.toString() === billingTypeFilter[0])?.name ?? 'Unknown')
                            : `${billingTypeFilter.length} selected`
                          }`
                        }
                        onDelete={() => setBillingTypeFilter([])}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {debouncedSearchField && (
                      <Chip
                        label={`Description: "${debouncedSearchField}"`}
                        onDelete={() => {
                          setSearchField(null);
                          setDebouncedSearchField(null);
                        }}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {salesRepFilter.length > 0 && (
                      <Chip
                        label={
                          `Sales Rep: ${salesRepFilter.length === 1
                            ? getSalesRepName(salesReps.find(sr => sr.id?.toString() === salesRepFilter[0]))
                            : `${salesRepFilter.length} selected`
                          }`
                        }
                        onDelete={() => setSalesRepFilter([])}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {(dateRangeFilter.startDateFrom || dateRangeFilter.startDateTo) && (
                      <Chip
                        label={
                          `Start Date: `
                          + `${dateRangeFilter.startDateFrom || '...'} - ${dateRangeFilter.startDateTo || '...'
                          }`}
                        onDelete={() => setDateRangeFilter(prev => ({
                          ...prev,
                          startDateFrom: '',
                          startDateTo: '',
                        }))}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {(dateRangeFilter.expiryDateFrom || dateRangeFilter.expiryDateTo) && (
                      <Chip
                        label={
                          `Expiry Date: `
                          + `${dateRangeFilter.expiryDateFrom || '...'} - ${dateRangeFilter.expiryDateTo || '...'
                          }`}
                        onDelete={() => setDateRangeFilter(prev => ({
                          ...prev,
                          expiryDateFrom: '',
                          expiryDateTo: '',
                        }))}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {(dateRangeFilter.rateExpiryDateFrom || dateRangeFilter.rateExpiryDateTo) && (
                      <Chip
                        label={
                          `Rate Expiry: `
                          + `${dateRangeFilter.rateExpiryDateFrom || '...'} - ${dateRangeFilter.rateExpiryDateTo || '...'
                          }`}
                        onDelete={() => setDateRangeFilter(prev => ({
                          ...prev,
                          rateExpiryDateFrom: '',
                          rateExpiryDateTo: '',
                        }))}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {(basePriceRange.min || basePriceRange.max) && (
                      <Chip
                        label={`Price: ${basePriceRange.min || '0'} - ${basePriceRange.max || '∞'}`}
                        onDelete={() => setBasePriceRange({ min: '', max: '' })}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>

      {loadedState === 'loading' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <Spinner state={loadedState} />
        </Box>
      )}
      {loadedState === 'loaded' && (
        <>
          {/* Search Results Summary */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {subscriptionMetadata?.totalRecords === 0 ? (
                'No subscriptions found'
              ) : (
                `Showing ${(page * limit) + 1}-${Math.min((page + 1) * limit, subscriptionMetadata?.totalRecords ?? 0)
                } of ${subscriptionMetadata?.totalRecords ?? 0} subscriptions`
              )}
              {activeFiltersCount > 0 && (
                ` (${activeFiltersCount} filter${activeFiltersCount > 1 ? 's' : ''} applied)`
              )}
            </Typography>
            {(searchField && searchField !== debouncedSearchField) && (
              <Typography variant="caption" color="primary">
                Searching...
              </Typography>
            )}
          </Box>
          <TablePagination
            component="div"
            sx={{
              '& p': { marginTop: '1rem' },
              position: 'sticky',
              top: 0,
              zIndex: 50,
              backgroundColor: 'white',
              borderBottom: '1px solid #e0e0e0',
            }}
            count={subscriptionMetadata?.totalRecords ?? 0}
            page={page}
            rowsPerPage={limit}
            showFirstButton
            showLastButton
            rowsPerPageOptions={rowsPerPageList}
            onPageChange={(e, newPage) => { setPage(newPage); }}
            onRowsPerPageChange={handleRowsPerPageChange}
          />

          <TableContainer
            component={Paper}
            elevation={12}
            sx={{
              mb: 1,
              maxHeight: '65vh',
            }}
          >

            {(subscriptions ?? []).length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  No subscriptions found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {activeFiltersCount > 0
                    ? 'Try adjusting your search criteria or clearing filters.'
                    : 'There are no subscriptions to display.'
                  }
                </Typography>
                {activeFiltersCount > 0 && (
                  <Button
                    variant="outlined"
                    onClick={clearAllFilters}
                    sx={{ mt: 2 }}
                    startIcon={<ClearIcon />}
                  >
                    Clear All Filters
                  </Button>
                )}
              </Box>
            ) : (
              <MuiTable
                stickyHeader
                style={{ borderCollapse: 'collapse' }}
              >
                <TableHead>
                  <StyledTableRow dark={true}>
                    <StyledTableCell
                      dark={true}
                      style={{ textAlign: 'center' }}
                    >
                      <TableSortLabel
                        active={sortBy === 'id'}
                        direction={getTableSortDirection('id')}
                        onClick={() => handleSort('id')}
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                        }}
                      >
                        ID
                      </TableSortLabel>
                    </StyledTableCell>

                    <StyledTableCell dark={true}>
                      <TableSortLabel
                        active={sortBy === 'c.name'}
                        direction={getTableSortDirection('c.name')}
                        onClick={() => handleSort('c.name')}
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                        }}
                      >
                        Client
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell dark={true}>
                      <TableSortLabel
                        active={sortBy === 'svc.invoiceItem'}
                        direction={getTableSortDirection('svc.invoiceItem')}
                        onClick={() => handleSort('svc.invoiceItem')}
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                        }}
                      >
                        Item
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell dark={true}>
                      <TableSortLabel
                        active={sortBy === 'invoiceDescription'}
                        direction={getTableSortDirection('invoiceDescription')}
                        onClick={() => handleSort('invoiceDescription')}
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                        }}
                      >
                        Description
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell dark={true}>
                      <TableSortLabel
                        active={sortBy === 'fq.name'}
                        direction={getTableSortDirection('fq.name')}
                        onClick={() => handleSort('fq.name')}
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                        }}
                      >
                        Freq.
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell dark={true}>
                      <TableSortLabel
                        active={sortBy === 'basePrice'}
                        direction={getTableSortDirection('basePrice')}
                        onClick={() => handleSort('basePrice')}
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                        }}
                      >
                        Base Price
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell dark={true}>
                      <TableSortLabel
                        active={sortBy === 'discountRate'}
                        direction={getTableSortDirection('discountRate')}
                        onClick={() => handleSort('discountRate')}
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                        }}
                      >
                        Discount Rate
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell dark={true}>
                      <TableSortLabel
                        active={sortBy === 'startDate'}
                        direction={getTableSortDirection('startDate')}
                        onClick={() => handleSort('startDate')}
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                        }}
                      >
                        Start
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell dark={true}>
                      <TableSortLabel
                        active={sortBy === 'expiryDate'}
                        direction={getTableSortDirection('expiryDate')}
                        onClick={() => handleSort('expiryDate')}
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                        }}
                      >
                        Expiry
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
                      Status
                    </StyledTableCell>
                    <StyledTableCell dark={true}>
                      <TableSortLabel
                        active={sortBy === 'rateExpiryDate'}
                        direction={getTableSortDirection('rateExpiryDate')}
                        onClick={() => handleSort('rateExpiryDate')}
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                        }}
                      >
                        Rate Expiry
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell dark={true}>
                      <TableSortLabel
                        active={sortBy === 'u.firstName'}
                        direction={getTableSortDirection('u.firstName')}
                        onClick={() => handleSort('u.firstName')}
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                        }}
                      >
                        Sales Rep
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell dark={true}>&nbsp;</StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {(subscriptions ?? []).map(
                    (s) => <SubscriptionListRow subscription={s} key={s.id} />,
                  )}
                </TableBody>
              </MuiTable>
            )}
          </TableContainer>
        </>
      )}

      <Stack
        direction="row"
        spacing={1}
        sx={{
          position: 'fixed',
          top: 84,
          right: 24,
          zIndex: 4000,
        }}
      >
        <Tooltip title="Go to superlift" arrow>
          <span>
            <Fab
              color="primary"
              aria-label="link to superlift"
              onClick={() => {
                window.open(
                  `https://superlift.theliftsystem.com/subscriptions/index`,
                  '_blank',
                );
              }}
            >
              <LinkIcon />
            </Fab>
          </span>
        </Tooltip>
        <Tooltip title="Create a new subscription" arrow>
          <span>
            <Fab
              color="primary"
              aria-label="create new sub"
              onClick={() => window.open(
                `/subscriptions/form`,
                '_blank',
              )}
            >
              <AddIcon />
            </Fab>
          </span>
        </Tooltip>
      </Stack>
    </>
  );
};
